import { FC, useState } from "react";
import { Loader2, X } from "lucide-react";
import { ConfirmationModal } from "src/modals/ConfirmationModal/ConfirmationModal";
import useRoles from "src/features/Permissions/hooks/useRoles";
import useUserRoles from "src/features/Permissions/hooks/useUserRoles";
import useAssignRole from "src/features/Permissions/hooks/useAssignRole";
import useRemoveRole from "src/features/Permissions/hooks/useRemoveRole";
import useUserDevices from "src/features/People/hooks/useUserDevices";
import useRemoveUserDevice from "src/features/People/hooks/useRemoveUserDevice";
import ImpersonationSection from "src/features/People/components/ImpersonationSection/ImpersonationSection";

interface Props {
	authUser: AuthUserInfo | null;
}

const formatDate = (value: string | null): string => {
	if (!value) return "Never";
	const date = new Date(value);
	return date.toLocaleString("en-GB", {
		day: "2-digit",
		month: "short",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit"
	});
};

const formatPlatform = (platform: string): string => {
	const lower = platform.toLowerCase();
	if (lower === "ios") return "iOS";
	if (lower === "android") return "Android";
	return platform;
};

const PersonAdminTab: FC<Props> = ({ authUser }) => {
	const [roleToRemove, setRoleToRemove] = useState<string | null>(null);
	const [deviceToRemove, setDeviceToRemove] =
		useState<AdminDeviceTokenResponse | null>(null);

	const userId = authUser?.id ?? "";
	const {
		data: userRoles,
		isLoading: isLoadingRoles,
		isError: isRolesError
	} = useUserRoles(userId);
	const { data: allRoles } = useRoles();
	const { mutate: assignRole } = useAssignRole(userId);
	const { mutate: removeRole, isLoading: isRemoving } = useRemoveRole(userId);
	const {
		data: devices,
		isLoading: isLoadingDevices,
		isError: isDevicesError
	} = useUserDevices(userId);
	const { mutate: removeDevice, isLoading: isRemovingDevice } =
		useRemoveUserDevice(userId);

	if (!authUser) {
		return (
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<p className="text-sm text-muted-foreground">
					No linked admin account.
				</p>
			</div>
		);
	}

	const assignedRoleNames = new Set(userRoles?.map((r) => r.name) ?? []);
	const availableRoles = (allRoles ?? []).filter(
		(r) => !assignedRoleNames.has(r.name)
	);

	const canImpersonate =
		userRoles?.some((r) => r.permissions.includes("CanImpersonate")) ?? false;

	const handleAssign = (roleName: string) => {
		assignRole({ userId, roleName });
	};

	const handleRemove = () => {
		if (roleToRemove) {
			removeRole(
				{ userId, roleName: roleToRemove },
				{
					onSuccess: () => setRoleToRemove(null)
				}
			);
		}
	};

	const handleRemoveDevice = () => {
		if (deviceToRemove) {
			removeDevice(
				{ userId, deviceId: deviceToRemove.id },
				{
					onSuccess: () => setDeviceToRemove(null)
				}
			);
		}
	};

	return (
		<div className="flex flex-col gap-4">
			{/* Account info */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Account
				</label>
				<div>
					<p className="text-sm font-medium text-zinc-600 mb-1">GUID</p>
					<p className="text-sm text-foreground">{authUser.username}</p>
				</div>
			</div>

			{/* Roles */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Roles
				</label>

				{isRolesError ? (
					<p className="text-sm text-red-600 mb-4">
						Could not load roles. Try refreshing the page.
					</p>
				) : isLoadingRoles ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
						Loading roles…
					</div>
				) : userRoles && userRoles.length > 0 ? (
					<div className="flex flex-wrap gap-2 mb-4">
						{userRoles.map((role) => (
							<span
								key={role.id}
								className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20"
							>
								{role.name}
								<button
									type="button"
									onClick={() => setRoleToRemove(role.name)}
									className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
									aria-label={`Remove ${role.name} role`}
								>
									<X className="h-3 w-3" />
								</button>
							</span>
						))}
					</div>
				) : (
					<p className="text-sm text-muted-foreground mb-4">
						No roles assigned.
					</p>
				)}

				{availableRoles.length > 0 && (
					<div>
						<label
							htmlFor="add-role-select"
							className="text-sm font-medium text-zinc-600 mb-1 block"
						>
							Add Role
						</label>
						<select
							id="add-role-select"
							className="w-full max-w-xs border border-border rounded-lg px-3 py-2 text-sm bg-white"
							value=""
							onChange={(e) => {
								if (e.target.value) handleAssign(e.target.value);
							}}
						>
							<option value="">Select a role...</option>
							{availableRoles.map((role) => (
								<option key={role.id} value={role.name}>
									{role.name}
								</option>
							))}
						</select>
					</div>
				)}
			</div>

			{/* Registered devices */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-1 block">
					Registered devices
				</label>
				<p className="text-sm text-zinc-600 mb-4">
					Devices receiving push notifications for this user. Remove a device if
					the user reports missing pushes — they will re-register next time they
					open the app.
				</p>

				{isDevicesError ? (
					<p className="text-sm text-red-600">
						Could not load devices. Try refreshing the page.
					</p>
				) : isLoadingDevices ? (
					<div className="flex items-center gap-2 text-sm text-muted-foreground">
						<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
						Loading devices…
					</div>
				) : devices && devices.length > 0 ? (
					<ul className="flex flex-col divide-y divide-border">
						{devices.map((device) => (
							<li
								key={device.id}
								className="flex items-center justify-between py-3 gap-4"
							>
								<div className="flex flex-col">
									<span className="text-sm font-medium text-foreground">
										{formatPlatform(device.platform)}
									</span>
									<span className="text-xs text-zinc-600">
										Added {formatDate(device.createdAt)}
									</span>
								</div>
								<button
									type="button"
									onClick={() => setDeviceToRemove(device)}
									className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-red-600 hover:bg-red-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
									aria-label={`Remove ${formatPlatform(device.platform)} device added ${formatDate(device.createdAt)}`}
								>
									<X className="h-3.5 w-3.5" aria-hidden="true" />
									Remove
								</button>
							</li>
						))}
					</ul>
				) : (
					<p className="text-sm text-muted-foreground">
						No devices registered.
					</p>
				)}
			</div>

			{canImpersonate && <ImpersonationSection impersonatorUserId={userId} />}

			{/* Dates */}
			<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
				<label className="font-heading text-lg font-medium text-foreground mb-4 block">
					Activity
				</label>
				<div className="flex flex-col gap-4">
					<div>
						<p className="text-sm font-medium text-zinc-600 mb-1">Last login</p>
						<p className="text-sm text-foreground">
							{formatDate(authUser.lastLogin)}
						</p>
					</div>
					<div>
						<p className="text-sm font-medium text-zinc-600 mb-1">
							Date joined
						</p>
						<p className="text-sm text-foreground">
							{formatDate(authUser.dateJoined)}
						</p>
					</div>
				</div>
			</div>

			<ConfirmationModal
				isOpen={roleToRemove !== null}
				title="Remove role?"
				description={`Remove the "${roleToRemove}" role from this user? They will lose the permissions associated with it.`}
				onCancel={() => {
					if (!isRemoving) setRoleToRemove(null);
				}}
				cancelBtnProps={{ disabled: isRemoving }}
				onSubmit={handleRemove}
				submitBtnProps={{ isLoading: isRemoving }}
			/>

			<ConfirmationModal
				isOpen={deviceToRemove !== null}
				title="Remove this device?"
				description="The user will stop receiving push notifications on this device until they re-open the app, which will re-register it."
				onCancel={() => {
					if (!isRemovingDevice) setDeviceToRemove(null);
				}}
				cancelBtnProps={{ disabled: isRemovingDevice }}
				onSubmit={handleRemoveDevice}
				submitBtnProps={{ isLoading: isRemovingDevice }}
			/>
		</div>
	);
};

export default PersonAdminTab;
